import { motion } from 'framer-motion';
import { ArrowRight, Check, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Hero({ t }: { t: any }) {
  return (
    <section className="relative overflow-hidden bg-zinc-950 pt-32 pb-20 lg:pt-48 lg:pb-32">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-500/20 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiB2aWV3Qm94PSIwIDAgNDAwIDQwMCI+PGZpbHRlciBpZD0iYSI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9Ii42IiBudW1PY3RhdmVzPSIzIiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgb3BhY2l0eT0iLjQiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')] opacity-20 brightness-100 contrast-150 mix-blend-overlay pointer-events-none" />
      
      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-emerald-400 backdrop-blur-sm mb-8"
          >
            <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            {t.hero.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-4xl text-5xl font-bold tracking-tight text-white sm:text-7xl"
          >
            {t.hero.title_prefix} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
              {t.hero.title_highlight}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 max-w-2xl text-lg leading-8 text-zinc-400"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 flex items-center gap-x-6"
          >
            <Link
              to="/register"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-emerald-500 px-8 py-4 text-base font-semibold text-white transition-all hover:bg-emerald-400 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(16,185,129,0.5)]"
            >
              <div className="absolute inset-0 flex translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              {t.hero.cta_primary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="#demo" className="flex items-center gap-2 text-sm font-semibold leading-6 text-white transition-colors hover:text-emerald-400">
              <PlayCircle className="h-5 w-5" />
              {t.hero.cta_secondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-12 flex flex-col items-center gap-4"
          >
            <p className="text-sm text-zinc-500">{t.hero.social_proof}</p>
            <div className="flex items-center gap-8 opacity-50 grayscale transition-all hover:grayscale-0">
               {/* Placeholder logos - using text for now to avoid broken images */}
               <span className="text-xl font-bold text-zinc-300">ACME Corp</span>
               <span className="text-xl font-bold text-zinc-300">Starlight</span>
               <span className="text-xl font-bold text-zinc-300">GlobalTech</span>
               <span className="text-xl font-bold text-zinc-300">Natura</span>
            </div>
          </motion.div>
          
          {/* Feature highlights mini-grid */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 w-full max-w-4xl border-t border-white/10 pt-12"
          >
             <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-2xl bg-zinc-900 border border-white/5 text-emerald-400">
                   <Check className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold">{t.hero.mini_features.instant.title}</h3>
                <p className="text-sm text-zinc-500">{t.hero.mini_features.instant.desc}</p>
             </div>
             <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-2xl bg-zinc-900 border border-white/5 text-emerald-400">
                   <Check className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold">{t.hero.mini_features.sources.title}</h3>
                <p className="text-sm text-zinc-500">{t.hero.mini_features.sources.desc}</p>
             </div>
             <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-2xl bg-zinc-900 border border-white/5 text-emerald-400">
                   <Check className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold">{t.hero.mini_features.security.title}</h3>
                <p className="text-sm text-zinc-500">{t.hero.mini_features.security.desc}</p>
             </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
